import React, { forwardRef } from 'react';
import { ThemeProvider } from 'styled-components';

export const green = '#1BB1A3';
export const white = 'white';
export const black = '#2c2d34';
export const grey = '#CCCCCC';
export const dark_grey = '#8E949C';
export const light_grey = '#edebeb';
export const red = '#E02020';
export const light_red = '#f86c6b';
export const dark_orange = '#D48000';
export const orange = '#FA6400';
export const yellow = '#F7B500';
export const blue = '#20a8d8';
export const cyan = '#63c2de';
export const dark_blue = '#3b7e93';
export const light_blue = '#e1eef2';
export const light_green = '#defae2';
export const obtuse_blue = '#6C757D';
export const grey_border = '#cfd8db';

// shared focus box-shadow colors, reused across button/input/select focus states
export const green_shadow = 'rgba(27, 177, 163, 0.5)';
export const blue_shadow = 'rgba(38, 143, 255, 0.5)';
export const yellow_shadow = 'rgba(248, 203, 0, 0.5)';
export const red_shadow = 'rgba(248, 108, 107, 0.5)';

// button hover colors
export const green_hover = '#13857a';
export const green_hover_border = '#127c72';
export const yellow_hover = '#c5a100';
export const yellow_hover_border = '#bb9900';
export const blue_hover = '#1985ac';
export const blue_hover_border = '#187fa3';
export const red_hover = '#f63c3a';
export const red_hover_border = '#f53231';

export const lightTheme = {
  page: {
    title: {
      color: black,
    },
  },
  form: {
    button: {
      file: {
        background: white,
        border: grey,
      },
      unSubmit: {
        background: dark_blue,
        color: white,
        borderColor: dark_blue,
      },
      unsubmit_action: {
        background: white,
        color: dark_blue,
        borderColor: dark_blue,
      },
      inactive: {
        background: obtuse_blue,
        color: white,
        borderColor: obtuse_blue,
      },
      inactive_action: {
        background: white,
        color: obtuse_blue,
        borderColor: obtuse_blue,
      },
      default: {
        background: white,
        color: green,
        borderColor: green,
        focusShadowColor: green_shadow,
      },
      success: {
        background: green,
        color: white,
        borderColor: green,
        hover: {
          background: green_hover,
          borderColor: green_hover_border,
        },
      },
      fill_green: {
        background: green,
        color: green,
        borderColor: green,
      },
      primary: {
        background: blue,
        color: white,
        borderColor: blue,
        focusShadowColor: blue_shadow,
        hover: {
          background: blue_hover,
          borderColor: blue_hover_border,
        },
      },
      warning: {
        background: yellow,
        color: white,
        borderColor: yellow,
        focusShadowColor: yellow_shadow,
        hover: {
          background: yellow_hover,
          borderColor: yellow_hover_border,
        },
      },
      primary_action: {
        background: white,
        color: blue,
        borderColor: blue,
      },
      warning_action: {
        background: white,
        color: yellow,
        borderColor: yellow,
      },
      danger: {
        background: light_red,
        color: white,
        borderColor: light_red,
        focusShadowColor: red_shadow,
        hover: {
          background: red_hover,
          borderColor: red_hover_border,
        },
      },
      skip: {
        background: white,
        color: black,
        borderColor: grey,
      },
      reject_action: {
        background: white,
        color: light_red,
        borderColor: light_red,
      },
    },
    error: {
      color: red,
    },
    checkbox: {
      color: dark_grey,
      disabled: grey,
    },
    select: {
      simple: {
        color: black,
        border: grey,
        background: white,
      },
    },
    input: {
      default: {
        background: white,
        color: black,
        border: grey,
      },
      focus: {
        background: white,
        color: black,
        border: blue,
        shadowColor: blue_shadow,
      },
      danger: {
        background: white,
        color: light_red,
        border: red,
        shadowColor: red_shadow,
      },
      disabled: {
        background: light_grey,
        color: dark_grey,
      },
    },
  },
  accounting: {
    actions: {
      color: dark_grey,
    },
    worker: {
      base: {
        color: dark_grey,
      },
      failure: {
        color: red,
      },
      need_action: {
        color: dark_orange,
      },
      success: {
        color: green,
      },
    },
    select: {
      borderColor: grey,
    },
    details: {
      content: {
        borderColor: grey,
      },
    },
    publish: {
      published: {
        color: green,
      },
      unpublished: {
        color: orange,
      },
    },
    download_report: {
      color: green,
    },
    notification: {
      text: {
        color: dark_orange,
      },
      button: {
        borderColor: black,
      },
      upload_button: {
        background: green,
        borderColor: green,
        color: white,
      },
      continue_button: {
        borderColor: grey,
      },
    },
    steps: {
      item: {
        color: dark_grey,
      },
      active_item: {
        color: black,
      },
      error: {
        color: red,
      },
      success: {
        color: green,
      },
      continue_button: {
        background: green,
        color: white,
        borderColor: green,
      },
      back_button: {
        color: dark_grey,
      },
      recalculate_button: {
        color: dark_grey,
      },
    },
  },
  modal: {
    screen: {
      background: white,
    },
  },
  media_asset: {
    status: {
      uploaded: red,
      uploading: yellow,
      ready: green,
      invalid_reference_short: dark_grey,
      empty: dark_grey,
      invalid_reference_long: dark_grey,
      approved: green,
      submitted: yellow,
      youtube_submitted: green,
      rejected: red,
      questionnaire_needed: yellow,
      inactive: obtuse_blue,
      default: grey,
    },
  },
  api_user_table: {
    status: {
      active: green,
      not_active: red,
    },
  },
  old_button: {
    color: cyan,
    borderColor: cyan,
  },
};

const withTheme = theme => Component => {
  return forwardRef((props, ref) => (
    <ThemeProvider theme={theme}>
      <Component {...props} ref={ref} />
    </ThemeProvider>
  ));
};

export default withTheme;
